/**
 * Bookmaker-API HTTP client — Lab NavigationService parity.
 *
 * Lab: POST `${adminurl}${path}` e.g. `http://127.0.0.1:3090/Market`
 * Paths are Entity/action only (`Market`, `Game/create`) — same as sevenone-1.
 * Default adminurl is the Nest host root (no `/api` or `/api/bookmaker` prefix).
 */

import { getAccessToken, isDemoSession } from '@/auth/oidc'
import { getLabAccessToken, getLabAdminUrl } from '@/config/lab'

export class BookmakerApiError extends Error {
  readonly status: number
  readonly body: unknown

  constructor(status: number, message: string, body: unknown) {
    super(message)
    this.name = 'BookmakerApiError'
    this.status = status
    this.body = body
  }
}

/** Lab `adminurl` (VITE_BOOKMAKER_BASE_URL / VITE_ADMIN_URL). */
export function getBookmakerBaseUrl(): string {
  return getLabAdminUrl()
}

/** Join base + Lab path (`Market/create` → `https://…/Market/create`). */
export function bookmakerUrl(labPath: string): string {
  const base = getBookmakerBaseUrl()
  const path = labPath.replace(/^\//, '')
  return `${base}/${path}`
}

function resolveLabToken(): string | null {
  return getLabAccessToken() || (isDemoSession() ? null : getAccessToken())
}

function isCrossOriginApi(): boolean {
  const base = getBookmakerBaseUrl()
  return /^https?:\/\//i.test(base)
}

function authHeaders(): HeadersInit {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  }
  const lab = getLabAccessToken()
  if (lab) {
    headers.Authorization = `Bearer ${lab}`
    return headers
  }
  if (isDemoSession()) return headers
  const token = getAccessToken()
  if (token) headers.Authorization = `Bearer ${token}`
  return headers
}

/** Lab attaches `_accessToken` on every POST except login / 2FA login verify. */
function withLabAccessToken(
  labPath: string,
  body: Record<string, unknown>,
): Record<string, unknown> {
  if (
    labPath === 'User/userLogin' ||
    labPath === 'User/verifySecreteKeyForLogin' ||
    labPath === 'User/checkUser2faEnabled' ||
    labPath.startsWith('User/userLogin')
  ) {
    return { ...body }
  }
  const token = resolveLabToken()
  if (!token) return { ...body }
  if (body._accessToken) return { ...body }
  return { ...body, _accessToken: token }
}

async function parseBody(res: Response): Promise<unknown> {
  const text = await res.text()
  if (!text.trim()) return null
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

function messageFromBody(data: unknown, status: number): string {
  if (data && typeof data === 'object') {
    const o = data as Record<string, unknown>
    if (typeof o.message === 'string' && o.message.trim()) return o.message
    if (Array.isArray(o.message) && o.message.length) return o.message.map(String).join('; ')
    if (typeof o.error === 'string' && o.error.trim()) return o.error
    if (typeof o.data === 'string' && o.data.trim()) return o.data
    if (typeof o.code === 'string' && o.code.trim()) return `${o.code} (${status})`
  }
  if (typeof data === 'string' && data.trim()) {
    // Lab sometimes returns JSON-encoded string bodies: "Error no accessToken"
    try {
      const inner = JSON.parse(data)
      if (typeof inner === 'string') return inner
    } catch {
      /* keep */
    }
    return data.replace(/^"|"$/g, '')
  }
  return `Bookmaker API ${status}`
}

function fetchInit(method: string, body?: string): RequestInit {
  return {
    method,
    headers: authHeaders(),
    body,
    // Cross-origin Nest host: omit cookies (token is Bearer / `_accessToken`).
    // `include` would require ACAO + Allow-Credentials on the API.
    credentials: isCrossOriginApi() ? 'omit' : 'same-origin',
  }
}

/** Lab-compatible POST — `${adminurl}${path}` e.g. `/Market`, `/Game/create`. */
export async function bmPost(labPath: string, body: Record<string, unknown> = {}): Promise<unknown> {
  const url = bookmakerUrl(labPath)
  const res = await fetch(url, fetchInit('POST', JSON.stringify(withLabAccessToken(labPath, body))))
  const data = await parseBody(res)
  if (!res.ok) {
    throw new BookmakerApiError(res.status, messageFromBody(data, res.status), data)
  }
  // Lab often returns HTTP 200 with { error, status: 400|500 } — still a failure for the UI.
  const labErr = labErrorFromBody(data, res.status)
  if (labErr) throw labErr
  return data
}

/**
 * Lab `labErr` / wohlig callbacks often use HTTP 200 + `{ error, status }`.
 * Callers (create/edit forms) must treat these as throws so they stay on the form.
 */
export function labErrorFromBody(data: unknown, httpStatus = 200): BookmakerApiError | null {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null
  const o = data as Record<string, unknown>
  const bodyStatus = typeof o.status === 'number' ? o.status : null
  const errText = typeof o.error === 'string' ? o.error.trim() : ''
  const failed =
    o.ok === false ||
    (bodyStatus != null && bodyStatus >= 400) ||
    errText.length > 0
  if (!failed) return null
  const status = bodyStatus != null && bodyStatus >= 400 ? bodyStatus : httpStatus
  return new BookmakerApiError(status, messageFromBody(o.data ?? o, status), data)
}

export async function bmDelete(entity: string, id: string): Promise<unknown> {
  return bmPost(`${entity.replace(/\/$/, '')}/delete`, { id })
}

export async function bmGet(labPath: string): Promise<unknown> {
  const url = bookmakerUrl(labPath)
  const res = await fetch(url, fetchInit('GET'))
  const data = await parseBody(res)
  if (!res.ok) {
    throw new BookmakerApiError(res.status, messageFromBody(data, res.status), data)
  }
  return data
}

/** Unwrap Lab / Bookmaker-API list payloads into row objects.
 *
 * Lab list controllers bind `result.data.result` (Angular `$http` → body.result):
 *   { result: [...], totalItems, itemsPerPage }
 */
export function unwrapBmList(data: unknown): Record<string, unknown>[] {
  if (data == null) return []
  if (data === 'noDataFound' || data === 'error') return []
  if (Array.isArray(data)) return data.filter(Boolean) as Record<string, unknown>[]

  if (typeof data === 'object') {
    const o = data as Record<string, unknown>

    // Lab paginated list: { result: Row[], totalItems, itemsPerPage }
    if (Array.isArray(o.result)) {
      return o.result.filter(Boolean) as Record<string, unknown>[]
    }

    // Nested: { data: { result: [...] } } or { value: { result: [...] } }
    for (const wrap of [o.data, o.value] as const) {
      if (wrap && typeof wrap === 'object' && !Array.isArray(wrap)) {
        const inner = wrap as Record<string, unknown>
        if (Array.isArray(inner.result)) {
          return inner.result.filter(Boolean) as Record<string, unknown>[]
        }
      }
    }

    for (const key of [
      'data',
      'value',
      'results',
      'rows',
      'items',
      'games',
      'markets',
      'events',
      'users',
      'list',
    ] as const) {
      const v = o[key]
      if (Array.isArray(v)) return v.filter(Boolean) as Record<string, unknown>[]
    }

    if (o.value && typeof o.value === 'object' && !Array.isArray(o.value)) {
      const nested = unwrapBmList(o.value)
      if (nested.length) return nested
    }
    if (o.data && typeof o.data === 'object' && !Array.isArray(o.data)) {
      const nested = unwrapBmList(o.data)
      if (nested.length) return nested
    }

    // Don't treat stub passthrough `{ ok, op, request }` as a table row
    if ('op' in o && 'request' in o && !('name' in o) && !('_id' in o) && !('betfairId' in o)) {
      return []
    }
    // Don't treat pagination meta-only objects as a row
    if ('totalItems' in o || 'itemsPerPage' in o) {
      return []
    }
    return [o]
  }
  return []
}

export function bmErrorMessage(e: unknown): string {
  if (e instanceof BookmakerApiError) return e.message
  if (e instanceof Error) return e.message
  return String(e)
}

/** Pull string form fields from a getOne / list entity. */
export function pickFormValues(
  data: unknown,
  keys: string[],
): Record<string, string> {
  const src =
    data && typeof data === 'object' && !Array.isArray(data)
      ? (data as Record<string, unknown>)
      : {}
  // Prefer nested value/data when Lab wraps the entity
  const entity =
    src.value && typeof src.value === 'object' && !Array.isArray(src.value)
      ? (src.value as Record<string, unknown>)
      : src.data && typeof src.data === 'object' && !Array.isArray(src.data)
        ? (src.data as Record<string, unknown>)
        : src
  const out: Record<string, string> = {}
  for (const k of keys) {
    const v = entity[k]
    out[k] = v == null ? '' : String(v)
  }
  return out
}

/**
 * Typed helpers — Lab path strings (`Market`, `Game/create`).
 * Full URL = `${VITE_BOOKMAKER_BASE_URL}/${path}` (Lab adminurl).
 */
export const bookmakerApi = {
  // Auth / user
  userLogin: (body: Record<string, unknown>) => bmPost('User/userLogin', body),
  userLogout: (body: Record<string, unknown> = {}) => bmPost('User/userLogout', body),
  changePassword: (body: Record<string, unknown>) => bmPost('User/changePassword', body),
  checkUser2faEnabled: (body: Record<string, unknown>) => bmPost('User/checkUser2faEnabled', body),
  verifySecreteKey: (body: Record<string, unknown>) => bmPost('User/verifySecreteKey', body),
  verifySecreteKeyForLogin: (body: Record<string, unknown>) =>
    bmPost('User/verifySecreteKeyForLogin', body),
  getSecreteKey: (body: Record<string, unknown> = {}) => bmPost('User/getSecreteKey', body),
  change2faAuthFlag: (body: Record<string, unknown>) => bmPost('User/change2faAuthFlag', body),
  getAllUsers: (body: Record<string, unknown> = {}) => bmPost('User/getAllUsers', body),
  /** Lab CreateMarketCtrl assignee list. */
  userGetAll: (body: Record<string, unknown> = {}) => bmPost('User/getAll', body),
  getOneUser: (body: Record<string, unknown>) => bmPost('User/getOneUser', body),
  createUser: (body: Record<string, unknown>) => bmPost('User/createUser', body),
  updateOneUser: (body: Record<string, unknown>) => bmPost('User/updateOneUser', body),
  deleteUser: (body: Record<string, unknown>) => bmPost('User/deleteUser', body),
  getCategoriesForTurnover: (body: Record<string, unknown> = {}) =>
    bmPost('User/getCategoriesForTurnover', body),
  getTurnOverForMarket: (body: Record<string, unknown>) =>
    bmPost('User/getTurnOverForMarket', body),

  // Game
  gameList: (body: Record<string, unknown> = {}) => bmPost('Game', body),
  /** Lab ListGames.getGames — GET Game (home /marketSelect). */
  /** Lab GET /Game — Bookmaker-API is POST-only; same as gameList. */
  gameListGet: () => bmPost('Game', {}),
  /** Lab edit game load — POST Game/game `{ id }`. */
  gameGetOne: (body: Record<string, unknown>) => bmPost('Game/game', body),
  gameCreate: (body: Record<string, unknown>) => bmPost('Game/create', body),
  gameEdit: (body: Record<string, unknown>) => bmPost('Game/edit', body),
  gameEnable: (body: Record<string, unknown>) => bmPost('Game/enablestatus', body),
  /** Lab game directive enable uses enableStatus (capital S). */
  gameEnableStatus: (body: Record<string, unknown>) => bmPost('Game/enableStatus', body),
  gameSearch: (body: Record<string, unknown>) => bmPost('Game/searchAllDataByname', body),
  gameDelete: (id: string) => bmDelete('Game', id),

  // Competition
  competitionList: (body: Record<string, unknown> = {}) => bmPost('Competition', body),
  /** Lab CreateEventCtrl — all competitions for dropdown. */
  competitionAll: (body: Record<string, unknown> = {}) => bmPost('Competition/competition', body),
  competitionCreate: (body: Record<string, unknown>) => bmPost('Competition/create', body),
  competitionEdit: (body: Record<string, unknown>) => bmPost('Competition/edit', body),
  competitionGetOne: (body: Record<string, unknown>) => bmPost('Competition/getOne', body),
  competitionGet: (body: Record<string, unknown>) => bmPost('Competition/getCompetition', body),
  competitionEnable: (body: Record<string, unknown>) => bmPost('Competition/enablestatus', body),
  competitionSelectMainHorseMarket: (body: Record<string, unknown>) =>
    bmPost('Competition/selectMainhorseMarket', body),
  competitionHorsePagination: (body: Record<string, unknown> = {}) =>
    bmPost('Competition/horsepagination', body),
  competitionDelete: (id: string) => bmDelete('Competition', id),

  // Event
  eventList: (body: Record<string, unknown> = {}) => bmPost('Event', body),
  /** Lab CreateMarketCtrl — events for dropdown. */
  eventAll: (body: Record<string, unknown> = {}) => bmPost('Event/event', body),
  eventCreate: (body: Record<string, unknown>) => bmPost('Event/create', body),
  eventEdit: (body: Record<string, unknown>) => bmPost('Event/edit', body),
  eventGetOne: (body: Record<string, unknown>) => bmPost('Event/getone', body),
  eventGet: (body: Record<string, unknown>) => bmPost('Event/getevent', body),
  eventEnable: (body: Record<string, unknown>) => bmPost('Event/enable', body),
  eventHorsePagination: (body: Record<string, unknown> = {}) =>
    bmPost('Event/horsepagination', body),
  eventDelete: (id: string) => bmDelete('Event', id),

  // Market
  marketList: (body: Record<string, unknown> = {}) => bmPost('Market', body),
  marketCreate: (body: Record<string, unknown>) => bmPost('Market/create', body),
  marketCreateJodi: (body: Record<string, unknown>) => bmPost('Market/createJodi', body),
  marketEdit: (body: Record<string, unknown>) => bmPost('Market/edit', body),
  marketEnable: (body: Record<string, unknown>) => bmPost('Market/enable', body),
  marketGetOne: (body: Record<string, unknown>) => bmPost('Market/getone', body),
  marketGetById: (body: Record<string, unknown>) => bmPost('Market/getbyid', body),
  marketGetOneJodi: (body: Record<string, unknown>) => bmPost('Market/getOnejodimarket', body),
  marketUpdateOne: (body: Record<string, unknown>) => bmPost('Market/marketUpdateOne', body),
  marketHorsePagination: (body: Record<string, unknown> = {}) =>
    bmPost('Market/horsepagination', body),
  marketResult: (body: Record<string, unknown>) => bmPost('Market/result', body),
  marketResultHorse: (body: Record<string, unknown>) =>
    bmPost('Market/resultforHorseRacing', body),
  marketJodiEdit: (body: Record<string, unknown>) => bmPost('Market/jodiMarketedit', body),
  marketDelete: (id: string) => bmDelete('Market', id),

  // Horse / jodiHorse
  horseList: (body: Record<string, unknown> = {}) => bmPost('Horse', body),
  horseCreate: (body: Record<string, unknown>) => bmPost('Horse/create', body),
  horseEdit: (body: Record<string, unknown>) => bmPost('Horse/edit', body),
  /** Lab Markets View rate edits — `Horse/horseEdit` (not `/edit`). */
  horseHorseEdit: (body: Record<string, unknown>) => bmPost('Horse/horseEdit', body),
  horseGetOne: (body: Record<string, unknown>) => bmPost('Horse/getOne', body),
  /** Lab Results modal — horses for a market (`Horse/getbyid`). */
  horseGetById: (body: Record<string, unknown>) => bmPost('Horse/getbyid', body),
  horseGetMultiple: (body: Record<string, unknown> | unknown[]) =>
    bmPost('Horse/getMultipleHorses', Array.isArray(body) ? { ids: body } : body),
  horseUpdateMany: (body: Record<string, unknown>) => bmPost('Horse/updateManyHorse', body),
  horseDelete: (id: string) => bmDelete('Horse', id),
  jodiHorseGetOne: (body: Record<string, unknown>) => bmPost('jodiHorse/getOne', body),
  jodiHorseEdit: (body: Record<string, unknown>) => bmPost('jodiHorse/jodihorseEdit', body),

  // Rates
  ratesGetAll: (body: Record<string, unknown> = {}) => bmPost('Rates/getAllRates', body),
  ratesSave: (body: Record<string, unknown>) => bmPost('Rates/saveRates', body),
  ratesDelete: (body: Record<string, unknown>) => bmPost('Rates/deleteRates', body),
  ratesDiffGetAll: (body: Record<string, unknown> = {}) =>
    bmPost('Ratesdiff/getAllRatesDiff', body),
  ratesDiffSave: (body: Record<string, unknown>) => bmPost('Ratesdiff/saveRatesDiff', body),
  ratesDiffDelete: (body: Record<string, unknown>) => bmPost('Ratesdiff/deleteRatesDiff', body),
  bothRatesGetAll: (body: Record<string, unknown> = {}) => bmPost('BothRates/getAllRates', body),
  bothRatesSave: (body: Record<string, unknown>) => bmPost('BothRates/saveRates', body),
  bothRatesDelete: (body: Record<string, unknown>) => bmPost('BothRates/deleteRates', body),
}
